import React from 'react';

const CookiePolicy: React.FC = () => {
  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Cookie Policy</h1>
        <p className="page-subtitle">How we use cookies and similar technologies</p>
        <p className="last-updated">Last updated: January 15, 2025</p>
      </div>

      <div className="page-content legal-content">
        <section className="content-section">
          <h2>1. What Are Cookies?</h2>
          <p>
            Cookies are small text files that are stored on your device (computer, mobile phone, or tablet)
            when you visit a website. They are widely used to make websites work more efficiently and provide
            information to website owners about user behavior and preferences.
          </p>
        </section>

        <section className="content-section">
          <h2>2. How We Use Cookies</h2>
          <p>
            Flow uses cookies and similar technologies to enhance your experience on our platform.
            These technologies help us understand how you use our service, remember your preferences,
            and provide you with a personalized experience.
          </p>
        </section>

        <section className="content-section">
          <h2>3. Types of Cookies We Use</h2>

          <h3>3.1 Essential Cookies</h3>
          <p>
            These cookies are necessary for the basic functionality of our website and cannot be disabled.
            They are usually set in response to actions you take, such as logging in or filling out forms.
          </p>
          <ul>
            <li><strong>Authentication:</strong> Remember your login status and keep you signed in</li>
            <li><strong>Security:</strong> Protect against cross-site request forgery and other security threats</li>
            <li><strong>Session Management:</strong> Maintain your session state across pages</li>
          </ul>

          <h3>3.2 Functional Cookies</h3>
          <p>
            These cookies enable enhanced functionality and personalization, such as remembering your
            preferences and settings.
          </p>
          <ul>
            <li><strong>User Preferences:</strong> Remember your editor settings, theme preferences, and language choices</li>
            <li><strong>Content Customization:</strong> Provide personalized content recommendations</li>
            <li><strong>Feature Enhancement:</strong> Enable advanced features like auto-save and draft recovery</li>
          </ul>

          <h3>3.3 Analytics Cookies</h3>
          <p>
            These cookies help us understand how visitors interact with our website by collecting and
            reporting information anonymously.
          </p>
          <ul>
            <li><strong>Usage Analytics:</strong> Track page visits, time spent, and user journeys</li>
            <li><strong>Performance Monitoring:</strong> Identify performance issues and optimize user experience</li>
            <li><strong>Feature Usage:</strong> Understand which features are most valuable to users</li>
          </ul>

          <h3>3.4 Marketing and Advertising Cookies</h3>
          <p>
            These cookies are used to deliver relevant advertisements and measure the effectiveness of
            advertising campaigns. We may use these with your consent.
          </p>
          <ul>
            <li><strong>Targeted Advertising:</strong> Show relevant ads based on your interests</li>
            <li><strong>Campaign Tracking:</strong> Measure the effectiveness of our marketing efforts</li>
            <li><strong>Social Media Integration:</strong> Enable sharing and social media features</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>4. Third-Party Cookies</h2>
          <p>
            Some cookies on our site are set by third-party services that we use to enhance functionality
            or analyze usage. These may include:
          </p>
          <ul>
            <li><strong>Google Analytics:</strong> For website analytics and user behavior insights</li>
            <li><strong>Payment Processors:</strong> For secure payment processing</li>
            <li><strong>Authentication Services:</strong> For social login options</li>
            <li><strong>Content Delivery Networks:</strong> For improved website performance</li>
          </ul>
          <p>
            These third-party services have their own privacy policies governing their use of cookies.
          </p>
        </section>

        <section className="content-section">
          <h2>5. Managing Your Cookie Preferences</h2>

          <h3>5.1 Browser Settings</h3>
          <p>
            Most browsers allow you to control cookies through their settings. You can typically:
          </p>
          <ul>
            <li>View which cookies have been set and delete them individually</li>
            <li>Block third-party cookies</li>
            <li>Block all cookies from specific sites</li>
            <li>Block all cookies from being set</li>
            <li>Delete all cookies when you close your browser</li>
          </ul>

          <h3>5.2 Flow Cookie Preferences</h3>
          <p>
            You can manage your cookie preferences specifically for Flow through our cookie consent
            banner or by accessing the cookie settings in your account preferences. You can choose to:
          </p>
          <ul>
            <li>Accept all cookies for the best experience</li>
            <li>Accept only essential cookies</li>
            <li>Customize which types of cookies to allow</li>
          </ul>

          <h3>5.3 Impact of Disabling Cookies</h3>
          <p>
            Please note that disabling certain cookies may affect the functionality of Flow:
          </p>
          <ul>
            <li>You may need to log in each time you visit</li>
            <li>Your preferences and settings may not be saved</li>
            <li>Some features may not work properly</li>
            <li>The user experience may be less personalized</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>6. Cookie Duration</h2>
          <p>
            Cookies can be categorized by how long they remain on your device:
          </p>

          <h3>6.1 Session Cookies</h3>
          <p>
            These are temporary cookies that are deleted when you close your browser. They are essential
            for basic website functionality during your visit.
          </p>

          <h3>6.2 Persistent Cookies</h3>
          <p>
            These cookies remain on your device for a set period (ranging from minutes to years) or
            until you delete them. They remember your preferences and settings across visits.
          </p>
        </section>

        <section className="content-section">
          <h2>7. Local Storage and Similar Technologies</h2>
          <p>
            In addition to cookies, we may use other technologies like local storage, session storage,
            and web beacons to enhance your experience:
          </p>
          <ul>
            <li><strong>Local Storage:</strong> Stores data locally for improved performance and offline functionality</li>
            <li><strong>Session Storage:</strong> Temporary storage that clears when you close your browser tab</li>
            <li><strong>Web Beacons:</strong> Small images used to track email opens and website visits</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>8. Cookie Policy Updates</h2>
          <p>
            We may update this Cookie Policy from time to time to reflect changes in our practices
            or for other operational, legal, or regulatory reasons. When we make changes, we will
            update the "last updated" date at the top of this policy.
          </p>
        </section>

        <section className="content-section">
          <h2>9. Your Rights</h2>
          <p>
            Depending on your location, you may have certain rights regarding cookies and data processing:
          </p>
          <ul>
            <li>Right to be informed about cookie usage</li>
            <li>Right to consent to or refuse non-essential cookies</li>
            <li>Right to withdraw consent at any time</li>
            <li>Right to access information about cookies we've set</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>10. More Information</h2>
          <p>
            For more information about cookies and how to manage them, you can visit:
          </p>
          <ul>
            <li><strong>About Cookies:</strong> www.aboutcookies.org</li>
            <li><strong>Your Online Choices:</strong> www.youronlinechoices.eu</li>
            <li><strong>Network Advertising Initiative:</strong> www.networkadvertising.org</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>11. Contact Us</h2>
          <p>
            If you have questions about our use of cookies or this Cookie Policy, please contact us:
          </p>
          <ul>
            <li>Email: privacy@flow-writing.com</li>
            <li>Through our <a href="/contact">Contact Us</a> page</li>
          </ul>
          <p>
            For more information about how we handle your personal data, please see our
            <a href="/privacy"> Privacy Policy</a>.
          </p>
        </section>
      </div>
    </div>
  );
};

export default CookiePolicy;